import { Fragment, useCallback, useEffect, useState } from "react";
import { MUTATIONS, useMutation } from "../../../../../api";
import useToggle from "../../../../../hooks/useToggle.js";
import HumanTime from "../../../../../lib/HumanTime.jsx";
import Avatar from "../../../../../design-system/components/Avatar/Avatar";
import TextWithNewlines from "../../../../../design-system/utils/TextWithNewLines/TextWithNewlines.jsx";
import { pluralize } from "../../../../../lib/text-utils.js";
import useIsInView from "../../../../../hooks/useIsInView.js";
import { useCaptcha } from "../../hooks/useCaptchaCheck.jsx";
import ReactionButton from "../../../../../design-system/components/ReactionButton/ReactionButton.jsx";
import { Confirm } from "../../../../../design-system/components/Confirm/Confirm.jsx";
import Button from "../../../../../design-system/components/Button/Button.jsx";
import { Variants } from "../../../../../design-system/components/Button/Variants.jsx";
import { Bubble } from "../../../../../design-system/components/Bubble/Bubble.jsx";
import Textarea from "../../../../../design-system/components/Textarea/Textarea.jsx";
import InputSubmit from "../../../../../design-system/components/InputSubmit/InputSubmit.jsx";
import InputText from "../../../../../design-system/components/InputText/InputText.jsx";
import styles from "./Comments.css.ts";

const UserCreateForm = ({
  onCreateComment,
  parentCommentId,
  label = "Dein Kommentar",
  me,
}) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim() || submitting) return;

    setSubmitting(true);
    onCreateComment({ content, parentCommentId })
      .then(() => {
        setContent("");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onChange = (e) => {
    setContent(e.target.value);
  };

  return (
    <form className={styles.formContainer} onSubmit={handleSubmit}>
      {me && <Avatar user={me} size={"30px"} />}
      <Textarea
        name="content"
        value={content}
        onChange={onChange}
        label={label}
        disabled={submitting}
        theme={"dark"}
        width100
        fullHeight={content.length > 50 || content.includes("\n")}
      />
      <InputSubmit value={"Absenden"} disabled={submitting} />
    </form>
  );
};

const AnonCreateForm = ({
  onCreateComment,
  parentCommentId,
  label = "Dein Kommentar",
}) => {
  const [showCaptcha, { on }] = useToggle();
  const [values, setValues] = useState({ content: "", name: "" });

  const handleVerify = (token) =>
    onCreateComment({ token, parentCommentId, ...values });

  const ref = useCaptcha({ onVerify: handleVerify });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!values.content.trim() || !values.name.trim()) return;
    on();
  };

  const onChange = (e) => {
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
  };

  return showCaptcha ? (
    <div ref={ref} />
  ) : (
    <form className={styles.formContainerColumn} onSubmit={handleSubmit}>
      <InputText
        name="name"
        value={values.name}
        onChange={onChange}
        label="Dein Name"
        theme={"dark"}
      />
      <Textarea
        name="content"
        value={values.content}
        onChange={onChange}
        label={label}
        theme={"dark"}
        fullHeight={
          values.content?.length > 50 || values.content?.includes("\n")
        }
      />
      <InputSubmit value={"Absenden"} />
    </form>
  );
};

const CreateCommentForm = ({ me, allowAnonUsers, ...rest }) => {
  if (!me && !allowAnonUsers) return null;
  if (!me) {
    return <AnonCreateForm {...rest} />;
  } else {
    return <UserCreateForm me={me} {...rest} />;
  }
};

const EditCommentForm = ({ comment, onClose, smallTeamId }) => {
  const [doUpdate] = useMutation(MUTATIONS.updateComment);
  const [content, setContent] = useState(comment.content);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim() || submitting) return;

    setSubmitting(true);
    doUpdate({ content, commentId: comment.id, smallTeamId })
      .then(onClose)
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onChange = (e) => {
    setContent(e.target.value);
  };

  return (
    <form className={styles.formContainer} onSubmit={handleSubmit}>
      <Textarea
        name="content"
        value={content}
        onChange={onChange}
        label={"Dein Kommentar"}
        theme={"dark"}
        width100
        fullHeight={content.length > 50 || content.includes("\n")}
      />
      <InputSubmit value={"Ändern"} disabled={submitting} />
    </form>
  );
};

const reactions = ["✨"];

const UnseenBadge = ({ onSeen, commentId }) => {
  const [visible, setVisible] = useState(false);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (visible && !triggered) {
      const timeoutId = setTimeout(() => {
        onSeen(commentId);
        setTriggered(true);
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [triggered, visible, onSeen, commentId]);

  const ref = useIsInView({
    onVisible: () => setVisible(true),
    onInvisible: () => setVisible(false),
  });
  return (
    <span ref={ref}>
      <Bubble text={"Neu!"} topRight={false} />
    </span>
  );
};

const CommentFooter = ({
  comment,
  showEdit,
  toggleEdit,
  showReplyForm,
  toggleReply,
  smallTeamId,
  canDelete,
}) => {
  const [doAdd] = useMutation(MUTATIONS.addCommentReaction);
  const [doRemove] = useMutation(MUTATIONS.removeCommentReaction);
  const [doDelete] = useMutation(MUTATIONS.deleteComment);

  if (comment.deletedAt) return null;

  const onAddReaction = (reaction) =>
    doAdd({
      reaction,
      commentId: comment.id,
      smallTeamId,
    });
  const onRemoveReaction = (reaction) =>
    doRemove({
      reaction,
      commentId: comment.id,
      smallTeamId,
    });
  return (
    <div className={styles.footer}>
      {reactions.map((r) => (
        <ReactionButton
          key={r}
          reaction={r}
          active={comment.myReaction === r}
          count={comment.reactionCounts[r] || 0}
          onClick={(e) => {
            e.stopPropagation();
            return comment.myReaction === r
              ? onRemoveReaction(r)
              : onAddReaction(r);
          }}
        />
      ))}
      {toggleReply && (
        <Button
          width={"fit-content"}
          variant={Variants.SMALL}
          onClick={toggleReply}
        >
          {showReplyForm ? "Abbrechen" : "Antworten"}
        </Button>
      )}
      {toggleEdit && (
        <Button
          width={"fit-content"}
          variant={Variants.SMALL}
          onClick={toggleEdit}
        >
          {showEdit ? "Editieren abbrechen" : "Editieren"}
        </Button>
      )}
      {canDelete && (
        <Confirm
          title={"Kommentar löschen?"}
          onConfirm={() => doDelete({ commentId: comment.id, smallTeamId })}
        />
      )}
    </div>
  );
};

const CommentHeader = ({ comment, isUnseen, onSeen }) => {
  const [hasBeenUnseen, setHasBeenUnseen] = useState(isUnseen);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isUnseen) setHasBeenUnseen(true);
  }, [isUnseen]);

  if (comment.deletedAt)
    return (
      <div className={styles.headerMeta}>
        gelöscht <HumanTime date={new Date(comment.deletedAt)} />
      </div>
    );

  return (
    <div className={styles.header}>
      <Avatar user={comment.author} size={"24px"} />
      <b>{comment.author.name}</b>
      {hasBeenUnseen && <UnseenBadge onSeen={onSeen} commentId={comment.id} />}
      <span className={styles.headerMeta}>
        <HumanTime date={comment.createdAt} />
        {comment.lastEditedAt && <> | editiert</>}
      </span>
    </div>
  );
};

const unknownAuthor = {
  id: "",
  name: "Unbekannt",
};

const Comment = ({
  comment,
  me,
  smallTeamId,
  isRoot,
  childComments,
  onCreateComment,
  unseenIds,
  onSeen,
  isPublic,
  isAdmin,
}) => {
  // Use unknown comment author, when original author was deleted.
  comment.author ||= unknownAuthor;

  const canEdit = comment.author.id === (me && me.id);
  const [showAllReplies, showAllRepliesActions] = useToggle();
  const [showEdit, showEditActions] = useToggle();
  const [showReplyForm, showReplyFormActions] = useToggle();

  const shownChildren =
    showAllReplies || childComments.length < 2
      ? childComments
      : childComments.slice(0, 1);
  const nonShownCount = childComments.length - shownChildren.length;

  const handleCreateComment = (data) =>
    onCreateComment(data).then(() => {
      showReplyFormActions.off();
      showAllRepliesActions.on();
    });

  const realShowReplyForm =
    onCreateComment && (showReplyForm || childComments.length > 0);

  const hasNew = childComments.slice(1).some((c) => unseenIds.has(c.id));

  // comment.deletedAt = true;

  return (
    <div className={styles.commentsContainer}>
      <div className={styles.commentContainer}>
        <CommentHeader
          comment={comment}
          isUnseen={unseenIds.has(comment.id)}
          onSeen={onSeen}
        />
        <div className={styles.content}>
          {!comment.deletedAt && !showEdit && (
            <TextWithNewlines as={Fragment}>{comment.content}</TextWithNewlines>
          )}
          {!comment.deletedAt && showEdit && (
            <EditCommentForm
              comment={comment}
              onClose={showEditActions.off}
              smallTeamId={smallTeamId}
            />
          )}
        </div>
        <CommentFooter
          comment={comment}
          showEdit={showEdit}
          toggleEdit={canEdit && showEditActions.toggle}
          isRoot={isRoot}
          showReplyForm={showReplyForm}
          toggleReply={
            childComments.length === 0 &&
            me &&
            onCreateComment &&
            showReplyFormActions.toggle
          }
          smallTeamId={smallTeamId}
          isPublic={isPublic}
          canDelete={isAdmin}
        />
      </div>

      {(childComments.length > 0 || realShowReplyForm) && (
        <div className={styles.threadContainer}>
          {shownChildren.length > 0 && (
            <div className={styles.commentsContainer}>
              {shownChildren.map((child) => (
                <Comment
                  key={child.id}
                  comment={child}
                  me={me}
                  smallTeamId={smallTeamId}
                  childComments={[]}
                  unseenIds={unseenIds}
                  onSeen={onSeen}
                  isPublic={isPublic}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
          {nonShownCount > 0 && (
            <Button variant={Variants.SMALL} onClick={showAllRepliesActions.on}>
              {pluralize(
                nonShownCount,
                `weitere${hasNew ? " neue " : " "}Antwort`,
                `weitere${hasNew ? " neue " : " "}Antworten`,
              )}{" "}
              sehen
            </Button>
          )}
          {realShowReplyForm && (
            <CreateCommentForm
              onCreateComment={handleCreateComment}
              parentCommentId={comment.id}
              me={me}
              label="Deine Antwort"
              allowAnonUsers={isPublic}
            />
          )}
        </div>
      )}
    </div>
  );
};

const Comments = ({
  comments,
  workshopId,
  sparkId,
  me,
  entryId,
  smallTeamId,
  myRoles,
  myCommentReactions,
  unseenComments,
  isPublic,
}) => {
  const rootComments = [];
  const childComments = {};
  const myReactions = myCommentReactions.reduce((m, r) => {
    m[r.commentId] = r.reaction;
    return m;
  }, {});
  const unseenIds = new Set(unseenComments.map((uc) => uc.commentId));
  comments.forEach((c) => {
    const nextC = { ...c, myReaction: myReactions[c.id] || null };
    if (c.parentCommentId) {
      (childComments[c.parentCommentId] =
        childComments[c.parentCommentId] || []).push(nextC);
    } else {
      rootComments.push(nextC);
    }
  });

  const [doAddComment] = useMutation(MUTATIONS.addComment);
  const handleCreateComment = ({ content, parentCommentId, token, name }) =>
    doAddComment({
      entryId,
      content,
      smallTeamId,
      workshopId,
      parentCommentId,
      sparkId,
      token,
      name,
    });

  const [doSee] = useMutation(MUTATIONS.seenComment);
  const handleSeen = useCallback(
    (commentId) => {
      doSee({ commentId, workshopId });
    },
    [workshopId, doSee],
  );
  const isAdmin = myRoles.includes("admin");

  return (
    <div className={styles.commentsContainer}>
      <div className={styles.commentsContainer}>
        {comments.length === 0 ? (
          <p className={styles.noCommentsNote}>Noch keine Kommentare</p>
        ) : (
          rootComments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              me={me}
              smallTeamId={smallTeamId}
              isRoot
              childComments={childComments[comment.id] || []}
              onCreateComment={handleCreateComment}
              unseenIds={unseenIds}
              onSeen={handleSeen}
              isPublic={isPublic}
              isAdmin={isAdmin}
            />
          ))
        )}
      </div>
      <CreateCommentForm
        onCreateComment={handleCreateComment}
        me={me}
        allowAnonUsers={isPublic}
      />
    </div>
  );
};

export default Comments;
